/**
 * Client-seitige Ableitung der 48h-Restlaufzeit von Job-Ergebnissen.
 *
 * Der Server räumt fertige Jobs 48 h nach Abschluss weg (CLEANUP_MAX_AGE_MS
 * in server/config/limits.js, Cleanup-Sweep stündlich). Wir spiegeln den
 * Wert hier als Konstante — reine Anzeige, keine Autorität: der Server
 * bleibt die Quelle der Wahrheit und löscht unabhängig von dieser Zahl.
 */

export const RESULT_TTL_MS = 48 * 60 * 60 * 1000; // Mockup + Template
export const DESIGN_RESULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** Unter dieser Restzeit kippt die Anzeige in den Warning-Ton (amber). */
export const EXPIRY_WARNING_MS = 12 * 60 * 60 * 1000;

export interface JobExpiry {
  /** Zeitpunkt an dem das Ergebnis verfällt (ms epoch). */
  expiresAt: number;
  /** Verbleibende Zeit, nie negativ. */
  remainingMs: number;
  /** Anteil der 48 h der noch übrig ist (0–1) — für den Restlaufzeit-Balken. */
  fractionRemaining: number;
  /** Aufgerundete verbleibende Stunden (min. 1 solange nicht abgelaufen). */
  hoursLeft: number;
  isExpired: boolean;
  /** true wenn < 12 h übrig (und noch nicht abgelaufen). */
  isWarning: boolean;
}

interface JobLike {
  status?: string | null;
  _type?: string | null;
  createdAt?: string | Date | null;
  completedAt?: string | Date | null;
  updatedAt?: string | Date | null;
}

/**
 * Restlaufzeit eines fertigen Jobs. Rechnet mit `completedAt` (Server setzt
 * das Feld beim Status-Flip auf "completed"); Fallback `updatedAt` für
 * Alt-Jobs, die das Feld noch nicht tragen — derselbe Fallback, den auch
 * der Server-Cleanup als Kriterium nutzt.
 *
 * Liefert `null` für nicht-fertige Jobs oder wenn kein Zeitstempel ableitbar
 * ist — Caller blendet die Anzeige dann aus.
 */
export function getJobExpiry(job: JobLike | null | undefined, now: number = Date.now()): JobExpiry | null {
  if (!job) return null;
  if (String(job.status || "").toLowerCase() !== "completed") return null;

  const ttlMs = job._type === "design" ? DESIGN_RESULT_TTL_MS : RESULT_TTL_MS;
  // Design cleanup is anchored to createdAt; mockup/template cleanup uses
  // the terminal timestamp with updatedAt as its legacy fallback.
  const doneRaw = job._type === "design" ? job.createdAt : job.completedAt || job.updatedAt;
  const doneAt = doneRaw ? new Date(doneRaw).getTime() : NaN;
  if (!Number.isFinite(doneAt)) return null;

  const expiresAt = doneAt + ttlMs;
  const remainingMs = Math.max(0, expiresAt - now);
  const isExpired = remainingMs <= 0;

  return {
    expiresAt,
    remainingMs,
    fractionRemaining: Math.max(0, Math.min(1, remainingMs / ttlMs)),
    hoursLeft: isExpired ? 0 : Math.max(1, Math.ceil(remainingMs / 3_600_000)),
    isExpired,
    isWarning: !isExpired && remainingMs < EXPIRY_WARNING_MS,
  };
}
